import { useContext, useEffect, useState, useRef, useCallback } from "react"

import { Box, Drawer, Stack, Typography } from "@mui/material"
import { alpha } from "@mui/material/styles"
import IframeResizer from "iframe-resizer-react"

import type { FC, ResponsiveImageType, iconKeys } from ".."
import { IconButton, Link, ResponsiveImage, SvgIcon } from ".."
import { SiteMetadataContext } from "../../local/wrappers/SiteMetadataProvider"
import { addQueryParams, type QueryParams } from "./helpers"

export type TripData = {
  image: ResponsiveImageType
  title: string
  slug: string
  primaryCountry: string
  destinationSlug: string
  id: string
}

type TripModalProps = {
  open: boolean
  onClose: () => void
  trip: TripData
}

const content = {
  title: "Start planning with Goway.",
  items: [
    {
      label: "Goway Destination Specialists",
      description:
        "Call Goway to talk with a Destination Specialist to start crafting your tailor-made vacation.",
      icon: "phone",
      link: {
        uri: "tel:0000000000",
        label: "0.000.000.0000",
      },
    },
    // {
    //   label: "Goway Video Calls",
    //   description: "Video call a Travel Specialist to begin planning your dream Goway vacation.",
    //   icon: "videoChat",
    //   link: {
    //     uri: "#",
    //     label: "Book Video Call",
    //   },
    // },
  ],
}

const TripModal: FC<TripModalProps> = ({ open, onClose, trip }) => {
  const siteMetadata = useContext(SiteMetadataContext)
  const [iframeReady, setIframeReady] = useState(false)
  const [formUrl, setFormUrl] = useState<string>("")
  const [variantUrl, setVariantUrl] = useState<string>("")
  const iframeContainerRef = useRef<HTMLDivElement>(null)

  const scrollToTop = useCallback(() => {
    if (iframeContainerRef.current) {
      iframeContainerRef.current.scrollTop = 0
    } else {
    }
  }, [])
  useEffect(() => {
    // Listen for the "scrollToTop" message from the iframe
    const handleScrollToTopMessage = (event: MessageEvent) => {
      if (event.data && event.data.action === "scrollToTop") {
        scrollToTop()
      }
    }
    window.addEventListener("message", handleScrollToTopMessage)
    return () => {
      window.removeEventListener("message", handleScrollToTopMessage)
    }
  }, [scrollToTop])

  useEffect(() => {
    setIframeReady(false)
  }, [onClose])

  useEffect(() => {
    const baseUrl = "https://forms.goway.com/recaptcha-t4-trip-inquiry-v2"
    const variantUrl = "https://forms.goway.com/variant-trip-specific"
    const queryParams: QueryParams = {
      Trip_Id__c: trip.id,
      Trip_Name__c: trip.title,
      Trip_URL__c: trip.slug,
      Trip_Primary_Country__c: trip.primaryCountry,
      Trip_Destination_Slug__c: trip.destinationSlug,
      pardotParameterMedium: "None",
      pardotParameterCampaign: "None",
      pardotParameterSource: "Direct",
    }

    setFormUrl(addQueryParams(baseUrl, queryParams))
    setVariantUrl(addQueryParams(variantUrl, queryParams))
  }, [])

  const modalContent = content
  modalContent.items[0].link.uri = siteMetadata.phoneUrl
  modalContent.items[0].link.label = siteMetadata.phone

  return (
    <Drawer
      open={open}
      onClose={onClose}
      anchor="right"
      slotProps={{
        backdrop: {
          sx: {
            bgcolor: (theme) => alpha(theme.palette["neutral.900"].main, 0.4),
          },
        },
      }}
      SlideProps={{
        timeout: 500,
      }}
      PaperProps={{
        elevation: 0,
        sx: {
          display: { md: "grid" },
          //gridTemplateColumns: { md: "clamp(350px, 35.15625%, 450px) 1fr" },
          borderRadius: (theme) => ({ xs: 0, md: theme.spacing(1.5, 0, 0, 1.5) }),
          maxWidth: { xs: "100%", md: 800 },
          width: "100%",
          height: { xs: "auto", md: "100%" },
          zIndex: 4100,
        },
      }}
      sx={{
        zIndex: 4000,
      }}
    >
      {/* <Stack
        rowGap={{ xs: 10, md: 27.5 }}
        justifyContent={{ md: "flex-start" }}
        bgcolor={(theme) => theme.palette["secondary.200"].main}
        color={(theme) => theme.palette["secondary.800"].main}
        px={{ xs: 6, md: 15 }}
        pb={{ xs: 10, md: 27.5 }}
        pt={{ xs: 20, md: 27.5 }}
        position="relative"
        display={{ xs: "none", md: "flex" }}
      >
        <Stack rowGap={3}>
          <Typography variant="heading.xl" component="h3">
            {modalContent.title}
          </Typography>
          <Typography variant="body.md" component="div" display={{ md: "none" }}>
            {modalContent.items[0].description}
          </Typography>
        </Stack>
        <Stack
          direction={{ xs: "row", md: "column" }}
          columnGap={17.5}
          sx={(theme) => ({
            "borderTop": { md: `1px solid ${alpha(theme.palette["neutral.900"].main, 0.1)}` },
            "borderBottom": { md: `1px solid ${alpha(theme.palette["neutral.900"].main, 0.1)}` },

            "> * + *": {
              borderTop: { md: `1px solid ${alpha(theme.palette["neutral.900"].main, 0.1)}` },
            },
          })}
        >
          {modalContent.items.map((item, i) => (
            <Stack key={i} py={{ md: 7.5 }} rowGap={7.5}>
              <Box key={i} display={{ xs: "none", md: "block " }}>
                <Typography variant="body.md" component="div" mb={2}>
                  {item.label}
                </Typography>
                <Typography variant="body.sm" component="div">
                  {item.description}
                </Typography>
              </Box>
              <Stack direction="row" alignItems="center" columnGap={3.5}>
                <SvgIcon variant={item.icon as iconKeys} size={20} />
                {item.link && (
                  <Link
                    type="base"
                    variant="body.sm"
                    sx={{ fontWeight: 500, color: (theme) => theme.palette["secondary.800"].main }}
                    href={item.link.uri}
                  >
                    {item.link.label}
                  </Link>
                )}
              </Stack>
            </Stack>
          ))}
        </Stack>
      </Stack> */}

      <Box
        maxHeight="100vh"
        ref={iframeContainerRef}
        sx={{
          overflowY: "auto",
        }}
      >
        <Stack
          direction={{ md: "row" }}
          p={{ xs: 4, md: 8 }}
          pb={4}
          justifyContent="space-between"
          gap={6}
        >
          {trip && (
            <Stack
              direction="row"
              alignItems="center"
              p={3}
              columnGap={7}
              sx={(theme) => ({
                flex: 1,
                bgcolor: theme.palette["secondary.200"].main,
                borderRadius: 1,
              })}
            >
              <ResponsiveImage image={trip.image} sx={{ width: 80, height: 80, borderRadius: 1 }} />
              <Box>
                <Typography
                  variant="body.xs"
                  fontWeight={500}
                  component="div"
                  mb={1}
                  sx={{ opacity: 0.7 }}
                >
                  Selected Trip Itinerary
                </Typography>
                <Typography variant="body.md" component="div">
                  {trip.title}
                </Typography>
              </Box>
            </Stack>
          )}
          <IconButton
            onClick={onClose}
            variant="base"
            icon={
              <SvgIcon
                variant="close"
                size={14}
                strokeMode
                sx={{ strokeWidth: 2, stroke: (theme) => theme.palette["secondary.800"].main }}
              />
            }
            sx={(theme) => ({
              "flex-shrink": 0,
              "border": { md: `1px solid ${alpha(theme.palette["secondary.800"].main, 0.1)}` },
              "position": { xs: "absolute", md: "static" },
              "top": { xs: 136, md: "unset" },
              "right": { xs: 16, md: "unset" },
              "width": { xs: 16, md: 52 },
              "height": { xs: 16, md: 52 },
            })}
          />
        </Stack>
        <Box px={{ xs: 4, lg: 8 }} pt={0} pb={{ xs: 10, lg: 8 }}>
          <Box
            component={IframeResizer}
            src={formUrl}
            onResized={() => setIframeReady(true)}
            className="t4-trip-enquiry"
            sx={{
              border: 0,
              width: "1px",
              minWidth: "100%",
              opacity: iframeReady ? 1 : 0,
              height: iframeReady ? "auto" : 0,
            }}
          />
          <Box
            component={IframeResizer}
            src={variantUrl}
            onResized={() => setIframeReady(true)}
            className="t4-trip-enquiry-variant"
            sx={{
              border: 0,
              width: "1px",
              minWidth: "100%",
              opacity: iframeReady ? 1 : 0,
              height: iframeReady ? "auto" : 0,
              display: "none",
            }}
          />
        </Box>
      </Box>
    </Drawer>
  )
}
export default TripModal
